import React, { useCallback, useRef, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import "easymde/dist/easymde.min.css";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { GeoFieldBase } from "@/src/components/form-fields/GeoField";
import {
  RestrictionPeriodFieldBase,
  toTemporalRestrictionFields,
} from "@/src/components/form-fields/RestrictionPeriodField";

import DropdownSeasonActions from "./DropdownSeasonActions";
import SeasonForm from "./SeasonForm";
import SmartlistAPI from "../../api/SmartlistAPI";
import { CONTENT_MAX_WIDTH_STYLE } from "../../constants/size";
import "../../css/admin/utils/item-status.scss";
import i18n from "../../i18n";
import { getStorefrontUrl } from "../../utils/data/Project";
import { generateNewSeason } from "../../utils/data/Series";
import ItemStatus from "../../utils/ItemStatus";
import { getDefaultSlug } from "../../utils/Translations";
import { isSmartlistItem } from "../../utils/typePredicate";
import { reorderRankedItems } from "../../utils/Utilities";
import Block from "../block/Block";
import ConfirmModal from "../modal/ConfirmModal";
import SmartlistCastingsBlock from "../smartlist/SmartlistCastingsBlock";
import SmartlistTranslations from "../smartlist/SmartlistTranslations";
import Tab from "../tabSelect/Tab";
import Tabs from "../tabSelect/Tabs";
import ContainerHeader from "../utils/ContainerHeader";
import FormNavigationHeader from "../utils/FormNavigationHeader";
import SwitchWithLabel from "../utils/SwitchWithLabel";

import type { DropResult } from "@hello-pangea/dnd";
import type { ChangeEvent, Dispatch, JSX, SetStateAction } from "react";

import type { GeoSelection } from "@/src/components/form-fields/GeoField";
import type { RestrictionPeriodChange } from "@/src/components/form-fields/RestrictionPeriodField";

import type {
  GeoblockMode,
  GetSmartlistResponse,
  SmartlistCasting,
  SmartlistItem,
  SmartlistItemUpdated,
  SmartlistSeason,
  SmartlistSeasonToCreate,
  SmartlistSeasonUpdated,
  SmartlistTranslation,
  UpdatedSmartlist,
} from "../../api/SmartlistAPI.types";
import type { PictureKeys } from "../../constants/ratio";
import type { AppState } from "../../reducers";
import type { ModalActions } from "../modal/Modal";
import type { SelectBoxDisplayItem } from "../selectBox/selectBox.types";
import type { SmartlistTranslationsHandle } from "../smartlist/SmartlistTranslations";

const navigationHeaderLinks = {
  en: "https://support.okast.tv/en/support/solutions/articles/103000097765-create-a-series-with-okast",
  fr: "https://support.okast.tv/fr/support/solutions/articles/103000097765-cr%C3%A9er-une-s%C3%A9rie-avec-okast",
};

const tabs = [
  { key: "contents", icon: "videocam", index: 0 },
  { key: "audios", icon: "queue_music", index: 1 },
];

interface SerieFormProps {
  serie: GetSmartlistResponse;
  setSerie: Dispatch<SetStateAction<UpdatedSmartlist>>;
  seasons: (SmartlistSeason | SmartlistSeasonUpdated)[];
  selectedSeasonIndex: number;
  setSelectedSeasonIndex: (index: number) => void;
  setSeasons: Dispatch<
    SetStateAction<(SmartlistSeason | SmartlistSeasonUpdated)[]>
  >;
  onDelSeason: (seasonUuid: string, serieUuid: string) => void;
  onAddSeason: (smartlistUuid: string, season: SmartlistSeasonToCreate) => void;
  onSubmit: (
    updatedSerie: UpdatedSmartlist,
    updatedSeasons: (SmartlistSeason | SmartlistSeasonUpdated)[],
    onSuccess: () => void,
  ) => void;
}

function SerieForm({
  serie,
  seasons,
  selectedSeasonIndex,
  setSelectedSeasonIndex,
  setSeasons,
  setSerie,
  onDelSeason,
  onAddSeason,
  onSubmit,
}: SerieFormProps): JSX.Element {
  const [openModal, setOpenModal] = useState(false);

  const { t } = useTranslation("editor-series");
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const serieTranslationsRef = useRef<SmartlistTranslationsHandle>(null);
  const seasonRefs = useRef<Map<string, SmartlistTranslationsHandle>>(
    new Map(),
  );
  // Out of state like the translations: an episode change must not re-render every season form.
  const seasonItemsRef = useRef<Map<string, SmartlistItemUpdated[]>>(new Map());

  const projects = useSelector(
    (state: AppState) => state.fetchReducer.projects,
  );
  const projectUuid = useSelector(
    (state: AppState) => state.architectureReducer.project_uuid,
  );

  const baseUrl = getStorefrontUrl(projectUuid, projects);

  const handleChangeTitle = (
    target: ChangeEvent<HTMLInputElement>["target"],
  ): void => {
    setSerie({
      ...serie,
      label: target.value,
    });
  };

  const handleSeasonsOrderChange = (value: boolean): void => {
    setSerie({
      ...serie,
      reversed: value,
    });
  };

  const handleDragEnd = (result: DropResult): void => {
    if (!result.destination) {
      return;
    }

    const items: SmartlistSeason[] = reorderRankedItems(
      seasons,
      result.source.index,
      result.destination.index,
    );

    const smartlistItems = serie.smartlist_items?.map((item) => {
      const itemChanged = items.find(
        (itemDrag) => itemDrag.uuid === item.content_uuid,
      );
      return {
        ...item,
        rank: itemChanged ? itemChanged.rank : item.rank,
      };
    });

    setSeasons(items);
    setSerie({
      ...serie,
      smartlist_items: smartlistItems || [],
    });
    setSelectedSeasonIndex(result.destination.index);
  };

  const handleChangeLibraryItems = useCallback(
    (
      seasonUuid: string,
      contents: (SelectBoxDisplayItem | SmartlistItem)[],
    ): void => {
      seasonItemsRef.current.set(
        seasonUuid,
        contents.map((content, idx) => {
          if (isSmartlistItem(content)) {
            return {
              content_type: content.content_type,
              content_uuid: content.content_uuid,
              rank: content.rank ?? idx,
            };
          }
          return {
            // itemType is only unset for not-yet-resolved items, which never
            // reach onChangeLibraryItems (only draggable/resolved items do).
            content_type: content.itemType as string,
            content_uuid: content.uuid,
            rank: content.rank ?? idx,
          };
        }),
      );
    },
    [],
  );

  const handleEpisodesOrderChange = useCallback(
    (uuid: string, value: boolean): void => {
      setSeasons((currentSeasons) =>
        currentSeasons.map((season) =>
          season.uuid === uuid ? { ...season, reversed: value } : season,
        ),
      );
    },
    [setSeasons],
  );

  const handleChangeSeasonCastings = useCallback(
    (uuid: string, castings: SmartlistCasting[]): void => {
      setSeasons((currentSeasons) =>
        currentSeasons.map((season) =>
          season.uuid === uuid ? { ...season, castings } : season,
        ),
      );
    },
    [setSeasons],
  );

  const handleGeoSelection = ({
    countries,
    countryGroups,
  }: GeoSelection): void => {
    setSerie({
      ...serie,
      countries,
      country_groups: countryGroups,
    });
  };

  const handleGeoOptions = (mode: GeoblockMode): void => {
    setSerie({
      ...serie,
      geoblock_mode: mode,
    });
  };

  const handleTempoSelection = (change: RestrictionPeriodChange): void => {
    setSerie({
      ...serie,
      ...toTemporalRestrictionFields(change),
    });
  };

  const handleChangeState = (enabled: boolean): void => {
    setSerie({
      ...serie,
      enabled,
    });
  };

  const handleCastingsChange = (castings: SmartlistCasting[]): void => {
    setSerie({
      ...serie,
      castings,
    });
  };

  const registerSeasonTranslations = useCallback(
    (uuid: string, handle: SmartlistTranslationsHandle | null): void => {
      if (handle) seasonRefs.current.set(uuid, handle);
      else seasonRefs.current.delete(uuid);
    },
    [],
  );

  const handleSave = async (): Promise<void> => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { translations, translations_indexes } =
      serieTranslationsRef.current?.getApiTranslations?.() ?? {
        translations: [],
        translations_indexes: {},
      };

    const [isSerieValid, ...seasonValidations] = await Promise.all([
      serieTranslationsRef.current?.isValid() ?? Promise.resolve(true),
      ...seasons.map(
        async (season) =>
          seasonRefs.current.get(season.uuid)?.isValid() ??
          Promise.resolve(true),
      ),
    ]);

    if (!isSerieValid || seasonValidations.some((isValid) => !isValid)) {
      const listOfInvalidSeasons = seasonValidations
        .map((isValid, index) => (!isValid ? index + 1 : null))
        .filter((index): index is number => index !== null);
      const invalidSeasonNumberString = listOfInvalidSeasons.join(", ");

      if (invalidSeasonNumberString) {
        toast.error(
          t("form:validationErrorSeasons", {
            seasonNumber: invalidSeasonNumberString,
            count: listOfInvalidSeasons.length,
          }),
        );
        return;
      }

      toast.error(t("form:validationError"));
      return;
    }

    const updatedSeasons = seasons.map((season) => {
      const seasonWithItems = {
        ...season,
        smartlist_items:
          seasonItemsRef.current.get(season.uuid) ?? season.smartlist_items,
      };
      const apiTranslations = seasonRefs.current
        .get(season.uuid)
        ?.getApiTranslations?.();
      if (!apiTranslations) return seasonWithItems;
      return {
        ...seasonWithItems,
        translations:
          apiTranslations.translations as unknown as SmartlistTranslation[],

        translations_indexes: apiTranslations.translations_indexes,
      };
    });

    const handleSuccess = (): void => {
      serieTranslationsRef.current?.onSaveSuccess();
      seasons.forEach((season) => {
        seasonRefs.current.get(season.uuid)?.onSaveSuccess();
      });
    };

    onSubmit(
      {
        ...serie,
        translations,
        translations_indexes,
      } as UpdatedSmartlist,
      updatedSeasons,
      handleSuccess,
    );
  };
  const handleAddSeason = (): void => {
    const newSeason = generateNewSeason(serie, seasons.length + 1);
    onAddSeason(serie.uuid, newSeason);
  };

  const handleDeleteSeason = (): void => {
    const seasonToDelete = seasons[selectedSeasonIndex];

    setOpenModal(false);

    if (seasonToDelete) {
      onDelSeason(seasonToDelete.uuid, serie.uuid);
    }
  };

  const handleChangeSeasonRank = (idx: number, value: string): void => {
    const newRank = parseInt(value, 10);

    const seasonsWithUpdatedRank = seasons.map((season, index) =>
      index === idx ? { ...season, rank: newRank } : season,
    );

    const seasonUuidToUpdate = seasons[idx]?.uuid;
    if (!seasonUuidToUpdate) return;

    const updatedSmartlistItems = serie.smartlist_items.map((item) =>
      item.content_uuid === seasonUuidToUpdate
        ? { ...item, rank: newRank }
        : item,
    );

    setSeasons(seasonsWithUpdatedRank);
    setSerie({
      ...serie,
      smartlist_items: updatedSmartlistItems,
    });
  };

  const handleChangeSeasonStatus = ({
    seasonId,
    enabled,
  }: {
    seasonId: string;
    enabled: boolean;
  }): void => {
    const updatedSeasons = seasons.map((season) => {
      if (season.uuid === seasonId) {
        return {
          ...season,
          enabled,
        };
      }
      return season;
    });
    setSeasons(updatedSeasons);
  };
  const onPreview = (
    translations: SmartlistTranslation[],
    uuid: string,
  ): void => {
    const seasonDefaultSlug = getDefaultSlug(translations);
    const url = `${baseUrl}/season/${seasonDefaultSlug ?? uuid}`;
    window.open(url, "_blank");
  };

  const onCopyUuid = async (uuid: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(uuid);
      toast.success(t("editor-content:success_copy"));
    } catch (err) {
      toast.error(t("editor-content:error_copy", { ns: "error" }));
      console.error("Failed to copy UUID: ", err);
    }
  };

  const uploadPicture = useMutation({
    mutationFn: async ({
      pictureFile,
      translationId,
      pictureFormat,
    }: {
      pictureFile: File;
      translationId: string;
      pictureFormat: PictureKeys;
    }) => {
      if (!projectSlug) {
        return Promise.reject(new Error("Project slug is missing"));
      }
      return SmartlistAPI.uploadPicture({
        smartlistId: serie.uuid,
        pictureFile,
        translationId,
        pictureFormat,
        projectSlug,
      });
    },
    onSuccess: (data, variables) => {
      const updatedTranslations = serie.translations.map((translation) => {
        if (translation.uuid === variables.translationId) {
          return { ...translation, picture: data.picture };
        }
        return translation;
      });
      setSerie({
        ...serie,
        translations: updatedTranslations,
      });
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const status = ItemStatus.getSmartlistStatus(serie);
  const serieDefaultSlug = getDefaultSlug(serie.translations);

  const breadcrumbs = [
    { name: t("editor-menu:publishing-management"), url: "/series" },
    { name: t("sectionTitle"), url: "/series" },
    { name: t("edit") },
  ];

  const modalActions: ModalActions[] = [
    {
      label: t("deleteCancel"),
      onClick: () => setOpenModal(false),
      buttonVariant: "secondary",
    },
    {
      label: t("deleteConfirm"),
      onClick: handleDeleteSeason,
      buttonVariant: "delete",
    },
  ];

  return (
    <div
      className="editor-series-form-container editor-has-breadcrumbs mx-auto flex max-w-(--maxWidth) flex-col gap-[0.9375rem]"
      style={CONTENT_MAX_WIDTH_STYLE}
    >
      <FormNavigationHeader
        breadcrumbs={breadcrumbs}
        links={navigationHeaderLinks}
      />
      <ContainerHeader
        withBackBtn
        defaultLabel={t("unamed")}
        item={serie}
        itemStatus={status}
        loading={uploadPicture.isLoading}
        onChangeLabel={handleChangeTitle}
        onChangeState={handleChangeState}
        onSave={handleSave}
        previewDisabled={status !== "active"}
        previewLink={`${baseUrl}/series/${serieDefaultSlug ?? serie.uuid}`}
        type="smartlist"
      />
      <SmartlistTranslations
        ref={serieTranslationsRef}
        baseUrl={baseUrl}
        smartlist={serie}
      />
      <Block title={t("seasons", { count: seasons?.length || 0 })}>
        <SwitchWithLabel
          isChecked={serie.reversed}
          label={t("editor-series:reverse_seasons")}
          name="reverse-seasons"
          onChange={handleSeasonsOrderChange}
        />
        <div className="mt-[0.625rem]">
          <Tabs
            onAddTab={handleAddSeason}
            onDragEnd={handleDragEnd}
            onSeasonStatusChange={handleChangeSeasonStatus}
            onSelectTab={setSelectedSeasonIndex}
            selectedIndex={selectedSeasonIndex}
            tabClassName="!border-1"
            tabSelectorClassName="!border-b-0 drop-shadow"
            actions={(index, data): JSX.Element => {
              const seasonData = data as SmartlistSeason;
              return (
                <DropdownSeasonActions
                  index={index}
                  setDeleteSeasonIndex={(): void => setOpenModal(true)}
                  onCopyUuid={async (): Promise<void> =>
                    onCopyUuid(seasonData.uuid)
                  }
                  onPreview={(): void =>
                    onPreview(seasonData.translations, seasonData.uuid)
                  }
                />
              );
            }}
          >
            {seasons?.map((season) => (
              <Tab
                key={season.uuid}
                data={season}
                inputValue={season.rank}
                label={t("seasonTitle")}
                onInputChange={handleChangeSeasonRank}
              >
                <SeasonForm
                  baseUrl={baseUrl}
                  onChangeCastings={handleChangeSeasonCastings}
                  onChangeEpisodesOrder={handleEpisodesOrderChange}
                  onChangeLibraryItems={handleChangeLibraryItems}
                  onRegisterTranslations={registerSeasonTranslations}
                  season={season}
                  tabs={tabs}
                />
              </Tab>
            ))}
          </Tabs>
        </div>
      </Block>
      <SmartlistCastingsBlock
        onChange={handleCastingsChange}
        value={serie.castings ?? []}
      />
      <Block title={t("restrictions")}>
        <div className="flex max-w-[500px] flex-col gap-8">
          <GeoFieldBase
            geoblockMode={serie.geoblock_mode}
            onModeChange={handleGeoOptions}
            onSelectionChange={handleGeoSelection}
            selectedCountries={serie.countries ?? []}
            selectedCountryGroups={serie.country_groups ?? []}
          />
          <RestrictionPeriodFieldBase
            beginDate={serie.begin_date}
            checked={serie.temporal_restriction}
            endDate={serie.end_date}
            label={t("editor-smartlist:availibility")}
            lang={i18n.language}
            onChange={handleTempoSelection}
          />
        </div>
      </Block>
      <ConfirmModal
        actions={modalActions}
        isOpen={openModal}
        onClose={(): void => setOpenModal(false)}
        size="md"
        title={t("deleteSeasonTitle")}
      >
        {t("deleteSeasonBody")}
      </ConfirmModal>
    </div>
  );
}

export default SerieForm;
